import React, { useEffect, useState, useMemo } from 'react';
import { Shuffle } from '@phosphor-icons/react';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import { sendDemoStepCompleteEmail } from '../../../UtilsEmail';

import {
  trackDemoAnalysisFinish,
  trackDemoAnalysisChangeClips,
  trackDemoGenerationStartSuccess,
  trackDemoGenerationStartError,
} from '../../../analytics';

import { FancyBtn } from '../../../components/custom';
import { Button } from '../../../components/general';
import { ContentHeader } from '../../../components/layout/content';

import useDemoData from '../../../hooks/useDemoData';
import { useWindowWidth } from '../../../hooks/useWindowWidth';

import { generateCreations } from '../../../services/api/creations';
import { sendSlackDemoNotification } from '../../../services/slackService';
import {
  getAnalysisData,
  getReviewData,
} from '../../../services/videoBuilderService';

import {
  PublicEvent,
  File,
  HlReel,
  Shot,
  ClipSelection,
  ModalError,
} from '../../../types';
import { isEmpty, getFileUrl, msecToSec, getBpWidth } from '../../../utils';

import PageSpinner from '../../General/pageSpinner';
import CreationPreview from '../../VideoBuilder/Analysis/components/creationPreview';
import { getCreationPreviewClips } from '../../VideoBuilder/Analysis/components/getPreviewData';
import DemoLoading from '../components/demoLoading';
import ReviewVideoBlock from '../components/reviewVideoBlock';

const analysingTipsList = [
  'Scanning the footage for familiar faces. If we blink, we might miss you',
  'Analysing millions of pixels to find your best moments. No pressure',
  'Our AI is connecting the dots, the frames, and possibly a few stars',
  'Looking for you in the footage. This is where the magic actually happens',
  'Peering into the video timeline like a digital detective with a space helmet',
  'Running face recognition at light speed. Pew pew',
  'Aligning clips, faces, and cosmic probabilities',
  'Teaching our AI what you look like. First impressions matter',
  'Searching through frames so you don’t have to',
  'Analysing footage one frame at a time. Time works differently in here',
  'Our AI is hard at work finding where you shine',
  'Zooming, scanning, and quietly nodding in approval',
];

interface Props {
  event: PublicEvent;
  prevStep: () => void;
  nextStep: () => void;
  setError: React.Dispatch<React.SetStateAction<ModalError>>;
}

const AnalyseStep: React.FC<Props> = (props) => {
  const { event, prevStep, nextStep, setError } = props;

  const { demoData, updateDemoData } = useDemoData();

  const eventId = event.eventId;

  const windowWidth = useWindowWidth();
  const mobilePrefix = useMemo(() => {
    return windowWidth < getBpWidth('sm') ? 'mobile_' : '';
  }, [windowWidth]);

  const [isAnalysing, setIsAnalysing] = useState(false);

  const [hlReel, setHlReel] = useState<HlReel | null>(null);
  const [requiredShots, setRequiredShots] = useState<Shot[]>([]);

  const [selectedClips, setSelectedClips] = useState<File[]>([]);
  const [otherMatchingClips, setOtherMatchingClips] = useState<File[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isPlay, setIsPlay] = useState(false);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchData(true);

      setIsLoading(false);
    };

    onMount();

    updateDemoData({ ...demoData, status: 'analysis' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // start videos playing
  useEffect(() => {
    if (!isLoading && !isAnalysing) setTimeout(() => setIsPlay(true), 500);
  }, [isLoading, isAnalysing]);

  // if analysing – re-fetch every 10 seconds
  useEffect(() => {
    if (isAnalysing) {
      const interval = setInterval(async () => await fetchData(), 10000);
      return () => clearInterval(interval);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalysing]);

  const fetchData = async (didMount?: boolean) => {
    try {
      const { isAnalysed, error } = await getAnalysisData({
        eventId,
        isPublic: true,
      });

      // if some assets missing – show error
      if (error) {
        setError({ subheading: 'Some assets are missing. Please try again' });
        return;
      }

      // update state (it re-fetches every 10 seconds when analysing)
      setIsAnalysing(!isAnalysed);

      if (isAnalysed) await onAnalysisFinish(didMount);
    } catch (err) {
      console.error(`Error fetching analysis data: ${err}`);
      setError({ subheading: 'Error fetching analysis data' });
    }
  };

  const onAnalysisFinish = async (didMount?: boolean) => {
    const { people, hlReel, requiredShots } = await getReviewData({
      eventId,
      isPublic: true,
    });

    setHlReel(hlReel);
    setRequiredShots(requiredShots);

    if (!didMount) {
      trackDemoAnalysisFinish();

      const freshDemoData = JSON.parse(
        localStorage.getItem('demoData') ?? '{}',
      );

      if (freshDemoData.notifications.analysis)
        sendDemoStepCompleteEmail(demoData.user.email, 'Analysis');
    }

    toast(
      <div className="text-sm">
        <b>Demo note</b>: This demo generates one personalised segment. In a
        real event, you can create multiple segments of different lengths.
      </div>,
      { position: 'bottom-right', autoClose: false },
    );

    const person = people.find(
      (p) => p.face.elementId === demoData.user.elementId,
    );

    const longestWildcard = Math.max(...requiredShots.map((s) => s.duration));

    const matchingClips =
      person?.allClips
        ?.filter((clip) =>
          (demoData.clips ?? []).includes(clip.parentElementId ?? ''),
        )
        .filter(
          (clip) =>
            msecToSec(clip.segment?.durationMilliseconds) >= longestWildcard,
        )
        .map((clip) => {
          const fileNameParts = clip.name.split('@') ?? [];
          const name = fileNameParts[0] + '@' + fileNameParts[1];
          return { ...clip, name };
        }) ?? [];

    setSelectedClips(matchingClips.slice(0, requiredShots.length));
    setOtherMatchingClips(matchingClips.slice(requiredShots.length));

    // if not enough clips – send back to upload
    if (matchingClips.length < requiredShots.length)
      setError({
        heading: 'We couldn’t find you this time',
        subheading: (
          <>
            Your photo or videos may not show you clearly enough for
            CrowdClip&reg; to make a confident match.
            <br />
            <br />
            Try uploading a front-facing selfie and at least one video where
            your face is visible.
          </>
        ),
        btn: (
          <Button onClick={prevStep} className="w-full">
            Return to Upload
          </Button>
        ),
      });
  };

  const clipSelection = useMemo(() => {
    let clipSelection: ClipSelection = {
      faceId: demoData.user.elementId,
      wildcardsFiller: [],
    };

    if (isEmpty(selectedClips) || isEmpty(requiredShots)) return clipSelection;

    clipSelection.wildcardsFiller = requiredShots.map((shot, idx) => ({
      wildCardShotIndex: shot.shotIndex,
      clipId: selectedClips[idx].elementId,
    }));

    return clipSelection;
  }, [demoData.user.elementId, requiredShots, selectedClips]);

  const previewClips = useMemo(() => {
    if (!hlReel) return [];

    const previewClips = getCreationPreviewClips({
      hlReel,
      clipSelection,
      selectedClips,
    });

    return previewClips;
  }, [hlReel, clipSelection, selectedClips]);

  const handleChangeClip = () => {
    const [firstSelected, ...restSelected] = selectedClips;
    const [firstOther, ...restOther] = otherMatchingClips;

    setSelectedClips([...restSelected, firstOther]);
    setOtherMatchingClips([...restOther, firstSelected]);

    trackDemoAnalysisChangeClips();

    // restart videos
    setIsPlay(false);
    setTimeout(() => setIsPlay(true), 100);
  };

  const handleStartGeneration = async () => {
    setIsSubmitting(true);

    try {
      await generateCreations(eventId, [clipSelection], true);

      trackDemoGenerationStartSuccess();
      await sendSlackDemoNotification({
        action: 'Started Generation',
        user: demoData.user,
      });

      nextStep();
    } catch (err: any) {
      trackDemoGenerationStartError(err.message);
      await sendSlackDemoNotification({
        action: 'Started Generation',
        user: demoData.user,
        details: err.message,
        isError: true,
      });
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <PageSpinner noTips />;

  if (isAnalysing)
    return (
      <DemoLoading
        type="analysis"
        heading="Analysing..."
        tipsList={analysingTipsList}
        expectedMin={10}
      />
    );

  const hlReelSrc = getFileUrl(
    hlReel?.parentElementId ?? '',
    mobilePrefix + (hlReel?.name ?? ''),
    'mp4',
  );

  const symbolClass =
    'w-4 h-[calc(10rem_*_16_/_9)] flex items-center justify-center text-xl font-bold';

  return (
    <React.Fragment>
      <ContentHeader title="Ready to generate your personalised reel" />

      <div className="text-lg">
        <b>We’ve identified where you appear</b> in the footage.
        <br />
        Next, we’ll insert your moments into the highlight reel to create your
        personalised video.
      </div>

      <div className="w-full flex flex-col sm:flex-row gap-4 sm:gap-8 justify-center">
        <div className="w-full sm:w-auto flex gap-4 sm:gap-8 justify-center">
          <ReviewVideoBlock
            heading="Highlight Reel"
            src={hlReelSrc}
            shouldPlay={isPlay}
          />

          <div className={symbolClass}>+</div>

          <ReviewVideoBlock
            heading="Your Clip"
            src={getFileUrl(
              selectedClips[0]?.parentElementId ?? '',
              selectedClips[0]?.name,
              'mp4',
            )}
            shouldPlay={isPlay}
            btn={
              !isEmpty(otherMatchingClips) && (
                <Button
                  onClick={handleChangeClip}
                  variant="link"
                  className="flex items-center gap-1 text-sm"
                >
                  <Shuffle />
                  <div>Change clips</div>
                </Button>
              )
            }
          />
        </div>

        <div className="flex gap-4 sm:gap-8 justify-center">
          <div className={symbolClass}>=</div>

          <ReviewVideoBlock
            heading="Personalised Video"
            videoEl={
              <CreationPreview
                hlReelSrc={hlReelSrc}
                previewClips={previewClips}
                className="w-full aspect-[9/16]"
                shouldPlay={isPlay}
              />
            }
          />

          {/* to be centred on mobile */}
          <div className={classNames(symbolClass, 'sm:hidden')}></div>
        </div>
      </div>

      <FancyBtn
        text="Generate"
        onClick={handleStartGeneration}
        isSubmitting={isSubmitting}
      />
    </React.Fragment>
  );
};

export default AnalyseStep;
